import { useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Star, Download, AlertTriangle, Loader2, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PHASE_COLORS = {
  'Warm-up': 'amber',
  'Technical': 'blue',
  'Tactical / SSG': 'emerald',
  'Conditioned game': 'rose',
  'Cool-down': 'slate',
};

const COMPLEXITY_COLORS = {
  Beginner: 'bg-green-100 text-green-800',
  Intermediate: 'bg-amber-100 text-amber-800',
  Advanced: 'bg-rose-100 text-rose-800',
};

function DrillCard({ drill, phaseIdx, drillIdx }) {
  const [open, setOpen] = useState(drillIdx === 0);
  return (
    <div
      className="border border-gray-200 rounded-xl overflow-hidden"
      data-testid={`ai-drill-${phaseIdx}-${drillIdx}`}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full px-4 py-3 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${COMPLEXITY_COLORS[drill.complexity] || 'bg-gray-100 text-gray-700'}`}>
              {drill.complexity}
            </span>
            <span className="text-xs text-gray-500">{drill.age_range}</span>
            <span className="text-xs text-gray-500">· {drill.duration_min} min</span>
            <span className="text-xs text-gray-500">· {drill.player_count_min}-{drill.player_count_max} players</span>
          </div>
          <p className="font-semibold text-gray-900">{drill.name}</p>
          <p className="text-sm text-gray-600 line-clamp-1">{drill.objective}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 space-y-3 bg-gray-50 text-sm">
          <div>
            <p className="font-medium text-gray-700 mb-1">Setup</p>
            <p className="text-gray-600 whitespace-pre-wrap">{drill.setup}</p>
          </div>
          {drill.coaching_points?.length > 0 && (
            <div>
              <p className="font-medium text-gray-700 mb-1">Coaching points</p>
              <ul className="list-disc list-inside text-gray-600 space-y-0.5">
                {drill.coaching_points.map((cp, i) => <li key={i}>{cp}</li>)}
              </ul>
            </div>
          )}
          {drill.equipment?.length > 0 && (
            <div>
              <p className="font-medium text-gray-700 mb-1">Equipment</p>
              <p className="text-gray-600">{drill.equipment.join(', ')}</p>
            </div>
          )}
          {drill.safety_notes?.length > 0 && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
              <p className="font-medium text-rose-800 mb-1 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" />Safety notes
              </p>
              <ul className="list-disc list-inside text-rose-700 space-y-0.5">
                {drill.safety_notes.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
          {(drill.progressions?.length > 0 || drill.regressions?.length > 0) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {drill.progressions?.length > 0 && (
                <div>
                  <p className="font-medium text-gray-700 mb-1">Progressions ↑</p>
                  <ul className="list-disc list-inside text-gray-600 space-y-0.5 text-xs">
                    {drill.progressions.map((p, i) => <li key={i}>{p}</li>)}
                  </ul>
                </div>
              )}
              {drill.regressions?.length > 0 && (
                <div>
                  <p className="font-medium text-gray-700 mb-1">Regressions ↓</p>
                  <ul className="list-disc list-inside text-gray-600 space-y-0.5 text-xs">
                    {drill.regressions.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function exportPdf(planDoc) {
  const plan = planDoc.plan || {};
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (need) => {
    if (y + need > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const writeText = (text, opts = {}) => {
    const size = opts.size || 10;
    const style = opts.style || 'normal';
    const color = opts.color || [30, 30, 30];
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(...color);
    const lines = doc.splitTextToSize(text, maxWidth);
    ensureSpace(lines.length * (size * 0.45) + 2);
    doc.text(lines, margin, y);
    y += lines.length * (size * 0.45) + (opts.gap ?? 2);
  };

  // Title
  writeText(plan.title || planDoc.name || 'AI Training Plan', { size: 18, style: 'bold', gap: 3 });
  writeText(`Generated: ${new Date(planDoc.created_at).toLocaleString('en-GB')}`, { size: 9, color: [120, 120, 120], gap: 1 });
  const params = planDoc.input_params || {};
  writeText(`Duration: ${plan.total_duration_min || params.duration_min} min · Players: ${params.player_count} · Age: ${params.age_group} · Focus: ${params.focus_area}${params.secondary_focus ? ` + ${params.secondary_focus}` : ''}`, { size: 9, color: [80, 80, 80], gap: 6 });

  // Disclaimer banner
  doc.setFillColor(254, 242, 242);
  doc.setDrawColor(244, 63, 94);
  const discLines = doc.splitTextToSize('AI-generated suggestions — review and adapt for the safety, age and ability of your players.', maxWidth - 6);
  const discHeight = discLines.length * 4 + 6;
  ensureSpace(discHeight);
  doc.rect(margin, y, maxWidth, discHeight, 'FD');
  doc.setFontSize(9);
  doc.setTextColor(159, 18, 57);
  doc.text(discLines, margin + 3, y + 5);
  y += discHeight + 4;

  // Summary
  if (plan.summary) {
    writeText('Session summary', { size: 12, style: 'bold', gap: 1 });
    writeText(plan.summary, { size: 10, gap: 5 });
  }

  // Phases
  (plan.phases || []).forEach((phase) => {
    writeText(`${phase.name} (${phase.duration_min} min)`, { size: 13, style: 'bold', color: [16, 110, 50], gap: 2 });
    (phase.drills || []).forEach((drill) => {
      writeText(`• ${drill.name} — ${drill.duration_min} min  [${drill.complexity} · ${drill.age_range}]`, { size: 10, style: 'bold', gap: 1 });
      writeText(`Objective: ${drill.objective}`, { size: 9, gap: 1 });
      writeText(`Setup: ${drill.setup}`, { size: 9, gap: 1 });
      if (drill.coaching_points?.length) {
        writeText(`Coaching points: ${drill.coaching_points.join(' · ')}`, { size: 9, gap: 1 });
      }
      if (drill.safety_notes?.length) {
        writeText(`Safety: ${drill.safety_notes.join(' · ')}`, { size: 9, color: [159, 18, 57], gap: 1 });
      }
      y += 2;
    });
    y += 3;
  });

  // Signature footer (signature box)
  ensureSpace(28);
  y = Math.max(y, pageHeight - 30);
  doc.setDrawColor(180, 180, 180);
  doc.line(margin, y, margin + 70, y);
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  doc.text('Reviewed and adapted by Coach (signature)', margin, y + 4);
  doc.line(pageWidth - margin - 40, y, pageWidth - margin, y);
  doc.text('Date', pageWidth - margin - 40, y + 4);

  const safe = (plan.title || 'training-plan').toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40);
  doc.save(`${safe}.pdf`);
}

export default function AiPlanResult({ planDoc, onUpdate, onDelete }) {
  const [busy, setBusy] = useState(false);
  if (!planDoc) return null;
  const plan = planDoc.plan || {};

  const toggleStar = async () => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/training/ai/plans/${planDoc.id}`, { starred: !planDoc.starred });
      onUpdate?.(res.data);
      toast.success(res.data.starred ? 'Starred' : 'Unstarred');
    } catch (e) {
      toast.error('Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this plan?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/training/ai/plans/${planDoc.id}`);
      toast.success('Plan deleted');
      onDelete?.(planDoc.id);
    } catch (e) {
      toast.error('Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="ai-plan-result">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <CardTitle className="text-2xl">{plan.title || planDoc.name}</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              {plan.total_duration_min} min · {planDoc.input_params?.player_count} players · {planDoc.input_params?.age_group} · Focus: {planDoc.input_params?.focus_area}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={toggleStar} disabled={busy} data-testid="ai-plan-star-btn">
              <Star className={`w-4 h-4 mr-1 ${planDoc.starred ? 'fill-amber-400 text-amber-500' : ''}`} />
              {planDoc.starred ? 'Starred' : 'Star'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportPdf(planDoc)} data-testid="ai-plan-pdf-btn">
              <Download className="w-4 h-4 mr-1" />PDF
            </Button>
            {onDelete && (
              <Button variant="outline" size="sm" onClick={handleDelete} disabled={busy} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="ai-plan-delete-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start gap-2 text-sm" data-testid="ai-plan-disclaimer">
          <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" strokeWidth={1.75} />
          <p className="text-rose-800">
            <strong>AI-generated suggestions.</strong> Review every drill and adapt it for the safety, age and ability of your players before delivering.
          </p>
        </div>

        {plan.summary && (
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
            <p className="text-sm text-gray-700">{plan.summary}</p>
          </div>
        )}

        <div className="space-y-4">
          {(plan.phases || []).map((phase, pi) => {
            const color = PHASE_COLORS[phase.name] || 'gray';
            return (
              <div key={pi} data-testid={`ai-plan-phase-${pi}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full bg-${color}-500`} />
                    {phase.name}
                  </h3>
                  <Badge variant="outline" className="text-xs">{phase.duration_min} min</Badge>
                </div>
                <div className="space-y-2">
                  {(phase.drills || []).map((drill, di) => (
                    <DrillCard key={di} drill={drill} phaseIdx={pi} drillIdx={di} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
